import { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBuilding, faChevronDown, faChevronUp, faTriangleExclamation, faCircleInfo,
} from "@fortawesome/free-solid-svg-icons";
import api from "../../services/api";
import styles from "./OrganizationAdminSection.module.css";

/**
 * Academia do professor — visível no painel admin, ao editar um professor.
 *
 * Professor sem academia: "Converter em academia" (a academia que hoje usa
 * o perfil de professor). Mesma operação do scripts/create_organization.py:
 * o professor vira dono e TODOS os alunos dele passam a ser da academia, com
 * ele como responsável. A conversão não tem desfazer pela interface, então
 * antes de confirmar a tela mostra o efeito real (simulação no backend,
 * dry_run) em vez de só um "tem certeza?".
 *
 * Props:
 *   professor – usuário em edição (UserOut: organization_id, organization_name, org_role)
 */
const ROLE_LABEL = { owner: "dono", manager: "gestor", professor: "professor" };

function errMsg(e, fallback) {
  const d = e?.response?.data?.detail;
  if (d && typeof d === "object") return d.message || fallback;
  return typeof d === "string" ? d : fallback;
}

export default function OrganizationAdminSection({ professor }) {
  const [aberto, setAberto] = useState(false);
  const [nome, setNome] = useState("");
  const [previa, setPrevia] = useState(null);
  const [simulando, setSimulando] = useState(false);
  const [convertendo, setConvertendo] = useState(false);
  const [erro, setErro] = useState("");
  const [convertida, setConvertida] = useState(null);
  const confirmarRef = useRef(null);

  useEffect(() => {
    if (previa && confirmarRef.current) confirmarRef.current.focus();
  }, [previa]);

  const orgName = convertida?.name || professor?.organization_name;
  const orgRole = convertida ? "owner" : professor?.org_role;
  const temAcademia = !!(convertida || professor?.organization_id);

  // Sem <form> de propósito: esta seção é renderizada DENTRO do <form> de
  // edição de usuário (UserFormTab). Form aninhado é HTML inválido e o
  // clique virava envio nativo do formulário externo (recarregava a página
  // em /?... sem chamar a API). Enter no campo aciona a simulação à mão.
  const simular = async () => {
    if (nome.trim().length < 2 || simulando) return;
    setSimulando(true);
    setErro("");
    try {
      const { data } = await api.post("/admin/organizations/convert", {
        professor_id: professor.id, name: nome.trim(), dry_run: true,
      });
      setPrevia(data);
    } catch (err) {
      setErro(errMsg(err, "Não foi possível simular a conversão."));
    } finally {
      setSimulando(false);
    }
  };

  const converter = async () => {
    setConvertendo(true);
    setErro("");
    try {
      const { data } = await api.post("/admin/organizations/convert", {
        professor_id: professor.id, name: nome.trim(),
      });
      setConvertida(data.organization);
      setPrevia(null);
    } catch (err) {
      setErro(errMsg(err, "Não foi possível converter em academia."));
    } finally {
      setConvertendo(false);
    }
  };

  return (
    <div className={styles.section}>
      <button type="button" className={styles.header} onClick={() => setAberto((v) => !v)} aria-expanded={aberto}>
        <span className={styles.title}><FontAwesomeIcon icon={faBuilding} /> Academia</span>
        <span className={styles.headerRight}>
          <span className={styles.status}>{temAcademia ? orgName : "Professor autônomo"}</span>
          <FontAwesomeIcon icon={aberto ? faChevronUp : faChevronDown} className={styles.chevron} />
        </span>
      </button>

      {aberto && (temAcademia ? (
        <div className={styles.infoBox} role={convertida ? "status" : undefined}>
          <FontAwesomeIcon icon={faCircleInfo} />
          <span>
            {convertida
              ? `Academia ${convertida.name} criada. ${professor.name} agora é o dono e já vê "Minha academia" no menu.`
              : `${professor.name} é ${ROLE_LABEL[orgRole] || "membro"} da academia ${orgName}. Professores e alunos são administrados pela própria academia, em "Minha academia".`}
          </span>
        </div>
      ) : (
        <>
          <p className={styles.helper}>
            Use quando uma academia já usa o TreinoNexo com este perfil de professor. {professor.name} vira o dono
            da academia e todos os alunos dele passam a ser da academia, continuando com ele como responsável.
          </p>

          {!previa ? (
            <div className={styles.form} role="group" aria-label="Converter em academia">
              <label htmlFor="org-convert-name" className={styles.label}>Nome da academia</label>
              <input
                id="org-convert-name"
                className={styles.input}
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); simular(); } }}
                placeholder="Ex.: Academia Movimento"
                minLength={2}
                maxLength={120}
                autoComplete="off"
              />
              {erro && <p className={styles.error} role="alert"><FontAwesomeIcon icon={faTriangleExclamation} /> {erro}</p>}
              <button type="button" className={`${styles.btn} ${styles.btnBlock}`} onClick={simular} disabled={simulando || nome.trim().length < 2}>
                {simulando ? "Calculando..." : "Converter em academia"}
              </button>
            </div>
          ) : (
            <div className={styles.confirmBox} role="group" aria-label="Confirmar conversão">
              <p className={styles.confirmText}>
                Criar a academia <strong>{previa.organization.name}</strong> com {professor.name} como dono?
              </p>
              <ul className={styles.impact}>
                <li>
                  {previa.students_count === 0
                    ? "Nenhum aluno vinculado a este professor ainda."
                    : `${previa.students_count} ${previa.students_count === 1 ? "aluno passa" : "alunos passam"} a ser da academia (${previa.active_students} ${previa.active_students === 1 ? "ativo" : "ativos"}).`}
                </li>
                <li>Treinos, dietas e histórico não mudam.</li>
                <li>A gestão de pagamento dos alunos por este professor deixa de valer.</li>
                <li>Desfazer só pelo suporte técnico.</li>
              </ul>
              {erro && <p className={styles.error} role="alert"><FontAwesomeIcon icon={faTriangleExclamation} /> {erro}</p>}
              <div className={styles.confirmActions}>
                <button type="button" className={styles.btn} onClick={() => { setPrevia(null); setErro(""); }} disabled={convertendo}>
                  Cancelar
                </button>
                <button ref={confirmarRef} type="button" className={`${styles.btn} ${styles.btnPrimary}`} onClick={converter} disabled={convertendo}>
                  {convertendo ? "Convertendo..." : "Criar academia"}
                </button>
              </div>
            </div>
          )}
        </>
      ))}
    </div>
  );
}
